<template>
  <header class="sticky top-0 z-40 border-b border-border/50 bg-background/80 backdrop-blur-md">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-4">
      <!-- Brand mark -->
      <RouterLink
        to="/"
        class="flex items-center gap-2.5 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
        aria-label="Ioniq 6 dashboard home"
      >
        <div class="w-7 h-7 rounded-lg bg-brand-navy flex items-center justify-center shrink-0">
          <svg
            class="w-4 h-4"
            viewBox="0 0 24 24"
            fill="#00AAD2"
            aria-hidden="true"
          >
            <path d="M13 2L4.5 13.5H11L9 22L19.5 10H13L13 2Z" />
          </svg>
        </div>
        <span class="font-semibold text-sm tracking-tight text-foreground hidden sm:block">Ioniq 6</span>
      </RouterLink>

      <!-- Desktop Navigation (hidden below md breakpoint via CSS; always in DOM for test selectors) -->
      <nav
        class="hidden lg:flex items-center gap-1"
        aria-label="Main navigation"
      >
        <template v-if="showNav">
          <RouterLink
            v-for="item in navItems"
            :key="item.path"
            :to="item.path"
            :class="navLinkClass(item.path)"
            :aria-current="ariaCurrent(item.path)"
          >
            {{ item.label }}
          </RouterLink>
        </template>
      </nav>

      <!-- Right: hamburger (mobile) + user + controls -->
      <div class="flex items-center gap-2 shrink-0">
        <!-- Hamburger button — only visible on mobile -->
        <button
          v-if="showNav"
          class="lg:hidden inline-flex items-center justify-center p-3 min-h-11 min-w-11 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          :aria-expanded="mobileNavOpen"
          :aria-controls="mobileNavOpen ? 'mobile-nav-panel' : undefined"
          :aria-label="mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'"
          @click="mobileNavOpen = !mobileNavOpen"
        >
          <MenuIcon v-if="!mobileNavOpen" class="w-5 h-5" aria-hidden="true" />
          <XIcon v-else class="w-5 h-5" aria-hidden="true" />
        </button>

        <span
          v-if="userName"
          class="text-xs text-muted-foreground hidden sm:block"
        >{{ userName }}</span>

        <button
          v-if="showSignOut"
          class="inline-flex items-center gap-1.5 min-h-11 min-w-11 px-3 py-2.5 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Sign out"
          @click="emit('sign-out')"
        >
          <LogOutIcon
            class="w-4 h-4"
            aria-hidden="true"
          />
          <span class="hidden sm:inline">Sign out</span>
        </button>

        <button
          v-if="showNav"
          class="p-3 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Open driver profile settings"
          @click="emit('open-profile')"
        >
          <SettingsIcon
            class="w-5 h-5"
            aria-hidden="true"
          />
        </button>

        <button
          :aria-pressed="isDark"
          :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
          class="p-3 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          @click="emit('toggle-dark')"
        >
          <SunIcon
            v-if="isDark"
            class="w-5 h-5"
            aria-hidden="true"
          />
          <MoonIcon
            v-else
            class="w-5 h-5"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  </header>

  <!-- Mobile slide-out drawer — teleported to body so it escapes z-40 sticky header -->
  <Teleport to="body">
    <Transition name="mobile-nav">
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -- dialog overlay requires keyboard handlers for escape/tab-trap -->
      <div
        v-if="mobileNavOpen && showNav"
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        class="fixed inset-0 z-50"
        @keydown.escape="mobileNavOpen = false"
        @keydown.tab="trapFocus"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-background/80 backdrop-blur-sm"
          aria-hidden="true"
          @click="mobileNavOpen = false"
        />
        <!-- Panel -->
        <nav
          id="mobile-nav-panel"
          ref="panelRef"
          class="absolute right-0 top-0 h-full w-72 max-w-[85vw] bg-background border-l border-border shadow-xl flex flex-col"
          aria-label="Mobile navigation"
          tabindex="-1"
        >
          <div class="h-14 px-4 flex items-center justify-between border-b border-border/50">
            <span class="text-sm font-semibold">Menu</span>
            <button
              class="p-2 min-h-11 min-w-11 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Close navigation menu"
              @click="mobileNavOpen = false"
            >
              <XIcon class="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
          <ul class="flex-1 overflow-y-auto py-2">
            <li v-for="item in navItems" :key="item.path">
              <RouterLink
                :to="item.path"
                :class="mobileLinkClass(item.path)"
                :aria-current="ariaCurrent(item.path)"
                @click="mobileNavOpen = false"
              >
                {{ item.label }}
              </RouterLink>
            </li>
          </ul>
        </nav>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { SunIcon, MoonIcon, LogOutIcon, SettingsIcon, MenuIcon, XIcon } from '@lucide/vue'

defineProps<{
  isDark: boolean
  userName?: string
  showNav?: boolean
  showSignOut?: boolean
}>()

const emit = defineEmits<{
  'toggle-dark': []
  'sign-out': []
  'open-profile': []
}>()

const route = useRoute()
const mobileNavOpen = ref(false)
const panelRef = ref<HTMLElement | null>(null)

const navItems = [
  { path: '/', label: 'Dashboard' },
  { path: '/trips', label: 'Trips' },
  { path: '/travel-log', label: 'Travel Log' },
  { path: '/charge-sessions', label: 'Charge History' },
  { path: '/charging-ecosystem', label: 'Ecosystem' },
  { path: '/geographic-efficiency', label: 'Geo Map' },
  { path: '/battery-health', label: 'Battery Health' },
  { path: '/ai-insights', label: 'AI Insights' },
  { path: '/energy', label: 'Energy Intelligence' },
  { path: '/maintenance', label: 'API Log' },
] as const

watch(mobileNavOpen, async (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
  if (open) {
    await nextTick()
    panelRef.value?.focus()
  }
})

onBeforeUnmount(() => {
  document.body.style.overflow = ''
})

function trapFocus(event: KeyboardEvent) {
  const panel = event.currentTarget as HTMLElement
  const focusables = Array.from(
    panel.querySelectorAll<HTMLElement>(
      'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"])',
    ),
  )
  if (focusables.length === 0) return

  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  const active = document.activeElement

  if (event.shiftKey) {
    if (active === first) {
      event.preventDefault()
      last.focus()
    }
  } else {
    if (active === last) {
      event.preventDefault()
      first.focus()
    }
  }
}

function navLinkClass(path: string): string {
  const base =
    'inline-flex items-center min-h-11 px-3 py-1.5 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
  const active = route.path === path
  return active
    ? `${base} bg-secondary text-foreground`
    : `${base} text-muted-foreground hover:text-foreground hover:bg-secondary/60`
}

function mobileLinkClass(path: string): string {
  const base =
    'block w-full px-4 py-3 min-h-11 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
  const active = route.path === path
  return active
    ? `${base} bg-secondary text-foreground`
    : `${base} text-muted-foreground hover:text-foreground hover:bg-secondary/60`
}

function ariaCurrent(path: string): 'page' | undefined {
  return route.path === path ? 'page' : undefined
}
</script>

<style>
.mobile-nav-enter-active,
.mobile-nav-leave-active {
  transition: opacity 0.2s ease;
}
.mobile-nav-enter-from,
.mobile-nav-leave-to {
  opacity: 0;
}
</style>
